import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const fallback =
  "https://cdn-icons-png.flaticon.com/512/149/149071.png";

export default function Profile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const [profile, setProfile] = useState(null);

  const [form, setForm] = useState({
    primeiro_nome: "",
    sobrenome: "",
    apelido: "",
    telefone: "",
    email: "",
    foto: ""
  });

  const isMobile = screenWidth <= 760;

  useEffect(() => {
    carregarPerfil();

    const resize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", resize);

    return () => {
      window.removeEventListener("resize", resize);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const carregarPerfil = async () => {
    try {
      setLoading(true);

      const res = await axios.get(
        "/auth/profile",
        {
          withCredentials: true
        }
      );

      setProfile(res.data);

      setForm({
        primeiro_nome: res.data.primeiro_nome || "",
        sobrenome: res.data.sobrenome || "",
        apelido: res.data.apelido || "",
        telefone: res.data.telefone || "",
        email: res.data.email || "",
        foto: res.data.foto || fallback
      });
    } catch (err) {
      console.log(err);
      navigate("/");
    }

    setLoading(false);
  };

  const updateForm = (e) => {
    const { name, value } = e.target;

    setForm({
      ...form,
      [name]: value
    });
  };

  const abrirSeletorImagem = () => {
    fileInputRef.current?.click();
  };

  const selecionarImagem = async (e) => {
    try {
      const file = e.target.files?.[0];

      if (!file) return;

      if (!file.type.startsWith("image/")) {
        alert("Escolha uma imagem válida");
        return;
      }

      const imagem = await comprimirImagem(file);

      setForm({
        ...form,
        foto: imagem
      });

      e.target.value = "";
    } catch (err) {
      console.log(err);

      alert(err.message || "Erro ao carregar imagem");
    }
  };

  const comprimirImagem = (file) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const imageUrl = URL.createObjectURL(file);

      img.onload = () => {
        const size = 420;
        let quality = 0.8;

        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");

        canvas.width = size;
        canvas.height = size;

        // Fundo branco para evitar partes pretas em PNG/transparência
        ctx.fillStyle = "#ffffff";
        ctx.fillRect(0, 0, size, size);

        const originalWidth = img.width;
        const originalHeight = img.height;

        const cropSize = Math.min(originalWidth, originalHeight);

        const sx = (originalWidth - cropSize) / 2;
        const sy = (originalHeight - cropSize) / 2;

        ctx.drawImage(
          img,
          sx,
          sy,
          cropSize,
          cropSize,
          0,
          0,
          size,
          size
        );

        let base64 = canvas.toDataURL("image/jpeg", quality);
        let tentativas = 0;

        while (base64.length > 700000 && tentativas < 10) {
          quality = Math.max(quality - 0.07, 0.35);
          base64 = canvas.toDataURL("image/jpeg", quality);
          tentativas++;
        }

        URL.revokeObjectURL(imageUrl);

        if (base64.length > 700000) {
          reject(
            new Error(
              "Imagem muito grande. Escolha uma imagem menor."
            )
          );

          return;
        }

        resolve(base64);
      };

      img.onerror = () => {
        URL.revokeObjectURL(imageUrl);

        reject(new Error("Erro ao carregar imagem"));
      };

      img.src = imageUrl;
    });
  };

  const salvarPerfil = async () => {
    try {
      if (!String(form.primeiro_nome || "").trim()) {
        alert("Digite seu nome");
        return;
      }

      setSaving(true);

      const res = await axios.put(
        "/auth/profile",
        {
          primeiro_nome: form.primeiro_nome,
          sobrenome: form.sobrenome,
          apelido: form.apelido,
          telefone: form.telefone,
          foto: form.foto
        },
        {
          withCredentials: true
        }
      );

      setProfile(res.data);

      setForm({
        primeiro_nome: res.data.primeiro_nome || "",
        sobrenome: res.data.sobrenome || "",
        apelido: res.data.apelido || "",
        telefone: res.data.telefone || "",
        email: res.data.email || "",
        foto: res.data.foto || fallback
      });

      alert("Perfil atualizado com sucesso");
    } catch (err) {
      console.log(err);

      const msg =
        err?.response?.data?.error ||
        "Erro ao salvar perfil";

      alert(msg);
    }

    setSaving(false);
  };

  const nomePreview = [
    form.primeiro_nome,
    form.sobrenome
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

  if (loading) {
    return (
      <div style={styles.page}>
        <p style={styles.loading}>
          Carregando perfil...
        </p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={selecionarImagem}
        style={styles.hiddenInput}
      />

      <div
        style={{
          ...styles.container,
          padding: isMobile ? 14 : 24
        }}
      >
        <div style={styles.header}>
          <p style={styles.tag}>
            Configurações da conta
          </p>

          <h1
            style={{
              ...styles.title,
              fontSize: isMobile ? 28 : 36
            }}
          >
            Meu perfil
          </h1>

          <p style={styles.subtitle}>
            Atualize sua imagem, nome, sobrenome, apelido e telefone.
          </p>
        </div>

        <div
          style={{
            ...styles.grid,
            gridTemplateColumns: isMobile
              ? "1fr"
              : "minmax(260px, 360px) 1fr"
          }}
        >
          <div style={styles.profileCard}>
            <div style={styles.avatarBox}>
              <img
                src={form.foto || fallback}
                alt="Foto do perfil"
                style={styles.avatar}
              />

              <button
                onClick={abrirSeletorImagem}
                style={styles.changePhotoButton}
              >
                Trocar imagem
              </button>
            </div>

            <div style={styles.previewInfo}>
              <h2 style={styles.previewName}>
                {nomePreview || profile?.nome || "Seu nome"}
              </h2>

              <p style={styles.previewNickname}>
                @{form.apelido || "apelido"}
              </p>

              <p style={styles.previewEmail}>
                {form.email}
              </p>

              <div style={styles.googleBadge}>
                O apelido inicial veio do Google, mas você pode alterar.
              </div>
            </div>
          </div>

          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>
              Dados do perfil
            </h2>

            <div
              style={{
                ...styles.formGrid,
                gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr"
              }}
            >
              <div style={styles.field}>
                <label style={styles.label}>
                  Nome
                </label>

                <input
                  name="primeiro_nome"
                  value={form.primeiro_nome}
                  onChange={updateForm}
                  placeholder="Digite seu nome"
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>
                  Sobrenome
                </label>

                <input
                  name="sobrenome"
                  value={form.sobrenome}
                  onChange={updateForm}
                  placeholder="Digite seu sobrenome"
                  style={styles.input}
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>
                  Apelido
                </label>

                <input
                  name="apelido"
                  value={form.apelido}
                  onChange={updateForm}
                  placeholder="Digite seu apelido"
                  style={styles.input}
                />

                <span style={styles.helpText}>
                  No primeiro acesso, o apelido vem do nome da conta Google.
                </span>
              </div>

              <div style={styles.field}>
                <label style={styles.label}>
                  Telefone
                </label>

                <input
                  name="telefone"
                  value={form.telefone}
                  onChange={updateForm}
                  placeholder="Ex: (16) 99999-9999"
                  style={styles.input}
                />
              </div>

              <div style={styles.fieldFull}>
                <label style={styles.label}>
                  Email
                </label>

                <input
                  value={form.email}
                  disabled
                  style={{
                    ...styles.input,
                    opacity: 0.65,
                    cursor: "not-allowed"
                  }}
                />
              </div>
            </div>

            <div style={styles.actions}>
              <button
                onClick={() => navigate("/dashboard")}
                style={styles.cancelButton}
                disabled={saving}
              >
                Voltar
              </button>

              <button
                onClick={salvarPerfil}
                style={{
                  ...styles.saveButton,
                  opacity: saving ? 0.7 : 1,
                  cursor: saving ? "not-allowed" : "pointer"
                }}
                disabled={saving}
              >
                {saving ? "Salvando..." : "Salvar perfil"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  page: {
    width: "100%",
    minHeight: "100vh",
    background: "#0f172a",
    color: "#fff",
    boxSizing: "border-box"
  },

  hiddenInput: {
    display: "none"
  },

  loading: {
    padding: 30,
    color: "#cbd5e1"
  },

  container: {
    width: "100%",
    maxWidth: 1180,
    margin: "0 auto",
    boxSizing: "border-box"
  },

  header: {
    marginBottom: 24
  },

  tag: {
    color: "#60a5fa",
    fontSize: 13,
    textTransform: "uppercase",
    letterSpacing: 1.4,
    marginBottom: 8
  },

  title: {
    margin: 0,
    color: "#f8fafc",
    lineHeight: 1.1
  },

  subtitle: {
    marginTop: 10,
    color: "#94a3b8",
    fontSize: 15,
    lineHeight: 1.5
  },

  grid: {
    display: "grid",
    gap: 22,
    alignItems: "start"
  },

  profileCard: {
    background: "linear-gradient(180deg, #111827, #020617)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 24,
    padding: 24,
    boxSizing: "border-box",
    boxShadow: "0 24px 70px rgba(0,0,0,0.35)"
  },

  avatarBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14
  },

  avatar: {
    width: 150,
    height: 150,
    borderRadius: "50%",
    objectFit: "cover",
    border: "3px solid rgba(96,165,250,0.4)",
    background: "#020617"
  },

  changePhotoButton: {
    padding: "11px 15px",
    borderRadius: 14,
    border: "1px solid rgba(96,165,250,0.35)",
    background: "rgba(37,99,235,0.18)",
    color: "#bfdbfe",
    cursor: "pointer",
    fontWeight: "bold"
  },

  previewInfo: {
    marginTop: 22,
    textAlign: "center"
  },

  previewName: {
    margin: 0,
    color: "#f8fafc",
    fontSize: 24
  },

  previewNickname: {
    margin: "8px 0 0",
    color: "#60a5fa",
    fontSize: 15,
    fontWeight: "bold",
    wordBreak: "break-word"
  },

  previewEmail: {
    margin: "8px 0 0",
    color: "#94a3b8",
    fontSize: 14,
    wordBreak: "break-word"
  },

  googleBadge: {
    marginTop: 16,
    padding: "10px 12px",
    borderRadius: 14,
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.08)",
    color: "#cbd5e1",
    fontSize: 13,
    lineHeight: 1.4
  },

  formCard: {
    background: "rgba(15,23,42,0.95)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: 24,
    padding: 24,
    boxSizing: "border-box"
  },

  formTitle: {
    margin: "0 0 18px",
    color: "#f8fafc",
    fontSize: 22
  },

  formGrid: {
    display: "grid",
    gap: 14
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: 7
  },

  fieldFull: {
    gridColumn: "1 / -1",
    display: "flex",
    flexDirection: "column",
    gap: 7
  },

  label: {
    color: "#cbd5e1",
    fontSize: 13
  },

  input: {
    width: "100%",
    padding: 14,
    borderRadius: 13,
    border: "1px solid #334155",
    background: "#020617",
    color: "#fff",
    outline: "none",
    boxSizing: "border-box",
    fontSize: 15
  },

  helpText: {
    color: "#64748b",
    fontSize: 12,
    lineHeight: 1.4
  },

  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    marginTop: 24,
    flexWrap: "wrap"
  },

  cancelButton: {
    padding: "12px 16px",
    borderRadius: 13,
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.04)",
    color: "#cbd5e1",
    cursor: "pointer"
  },

  saveButton: {
    padding: "12px 18px",
    borderRadius: 13,
    border: "none",
    background: "#2563eb",
    color: "#fff",
    fontWeight: "bold"
  }
};

